import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCheck, FaChevronDown, FaPen, FaPlus } from "react-icons/fa";
import { IoClose, IoPersonOutline } from "react-icons/io5";
import { GoPencil } from "react-icons/go";
import { RxPerson } from "react-icons/rx";
import { _imagesURL } from "../../constants/API";
import { DOCTOR_ACCOUNT } from "../../constants/RoutesUrl";
import { apiAdmin, getSplashData, getApiErrorMessage } from "../../services/api";
import ImageCropModal from "../common/ImageCropModal";
import { Language, LoginUserData } from "../../App";


// Used only if SplashData hasn't loaded into localStorage yet.
const FALLBACK_ACCOUNT_DETAILS = {
  fullName: "Dr. Jane Smith",
  workEmail: "janesmith@hospital.com",
  countryCode: "+91",
  phone: "332 392 3992",
  country: "Spain",
};

// Same SplashData.user shape used by the account settings pages
// (general_profile.jsx: firstname/lastname/user_title, email, country_code,
// phone, location) — reused here instead of hardcoding.
function getAccountDetails() {
  try {
    const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
    if (!user) return FALLBACK_ACCOUNT_DETAILS;
    const fullName =
      [user.firstname, user.lastname].filter(Boolean).join(" ") ||
      FALLBACK_ACCOUNT_DETAILS.fullName;
    return {
      fullName,
      workEmail: user.email || FALLBACK_ACCOUNT_DETAILS.workEmail,
      countryCode: user.country_code || FALLBACK_ACCOUNT_DETAILS.countryCode,
      // Stays empty if the user hasn't set a phone — `||` would have masked
      // a real empty value with the placeholder number since "" is falsy.
      phone: user.phone ?? "",
      // Same deal for `country` — stays empty if the user hasn't set one,
      // rather than falling back to a placeholder like `location` would.
      country: user.location ?? "",
    };
  } catch (e) {
    return FALLBACK_ACCOUNT_DETAILS;
  }
}

// SplashData.user.image is stored as a relative path — same _imagesURL
// prefix NavBar uses for the account avatar. Null when there's no image so
// the caller falls back to the upload UI.
function getSplashProfileImage() {
  try {
    const user = JSON.parse(localStorage.getItem("SplashData"))?.user;
    return user?.mentor_profile?.profile_picture ? `${_imagesURL}${user.mentor_profile.profile_picture}` : null;
  } catch (e) {
    return null;
  }
}

// The applied/approved mentor_profile persists server-side and survives a
// refresh, unlike the `initialData` prop (only set from local state right
// after a create-mode submission within the same session) — so edit mode
// falls back to this whenever the caller doesn't hand over fresher data.
function getSplashMentorFormData() {
  try {
    const profile = JSON.parse(localStorage.getItem("SplashData"))?.user?.mentor_profile;
    if (!profile) return null;
    return {
      specialty: profile.specialty || "",
      experience: profile.experience || "",
      institution: profile.institute || "",
      mentorshipType: profile.type || "One-To-One",
      price: profile.price || "",
      duration: profile.duration || "60",
      bio: profile.bio || "",
      focusList: profile.focus ? profile.focus.split("&&&&").filter(Boolean) : [],
    };
  } catch (e) {
    return null;
  }
}

// Used only if SplashData hasn't loaded into localStorage yet (e.g. very
// first paint before the splash query resolves).
const FALLBACK_SPECIALTY_OPTIONS = [
  "Ophthalmology",
  "Cirugía plástica ocular y orbitaria",
  "Cirugía refractiva",
  "Cirugía vitreoretina",
  "Glaucoma",
  "Uveítis",
  "Superficie ocular y córnea",
  "Pediátrica",
  "Cirugía facorefractiva o cataratas",
];

// SplashData (set on login and refreshed by NavBar's splash query) carries
// the real subcategory list under home.subcategories — use that instead of
// a hardcoded list so it always matches what the API actually offers.
function getSpecialtyOptions() {
  try {
    const splash = JSON.parse(localStorage.getItem("SplashData"));
    const subcategories = splash?.home?.subcategories;
    if (Array.isArray(subcategories) && subcategories.length) {
      return subcategories.map((sub) => sub.name).filter(Boolean);
    }
  } catch (e) {
    // fall through to the static fallback below
  }
  return FALLBACK_SPECIALTY_OPTIONS;
}

// 1-2, 2-3, 3-4 ... 19-20, then a final 20+ catch-all. Takes the "years"
// word as a param (rather than being a module-level constant) so it can be
// rebuilt with the current language's translation whenever `lang` changes.
function getExperienceOptions(yearsLabel) {
  const options = [];
  for (let start = 1; start <= 19; start += 1) {
    options.push(`${start}-${start + 1} ${yearsLabel}`);
  }
  options.push(`20+ ${yearsLabel}`);
  return options;
}

const inputStyle = {
  width: "100%",
  border: "1px solid #E6E7EA",
  borderRadius: "8px",
  padding: "10px 14px",
  fontSize: "13px",
  color: "#0F1A2B",
  outline: "none",
  background: "#fff",
};

const labelClass = "fs-14 fw-500 d-block mb-2";

// Only value on offer right now — kept as an array (not a bare string) so the
// mentorship type field can render as a real dropdown like the others.
const MENTORSHIP_TYPE_OPTIONS = ["One-To-One"];

const EMPTY_FORM = {
  specialty: "",
  experience: "",
  institution: "",
  mentorshipType: "One-To-One",
  price: "",
  duration: "60",
  bio: "",
};

// How long the slide-out transition takes — must match the Offcanvas'
// `transition: transform ${CLOSE_ANIMATION_MS}ms ease-in-out` below.
// Close is deliberately animated first, then unmounted, so it never just
// vanishes the way it used to.
const CLOSE_ANIMATION_MS = 300;

function CustomSelect({ value, onChange, options, placeholder, error }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="position-relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="d-flex align-items-center justify-content-between roboto-regular w-100"
        style={{
          ...inputStyle,
          textAlign: "left",
          color: value ? "#0F1A2B" : "#98A2B3",
          cursor: "pointer",
          border: error ? "1px solid #ef4444" : inputStyle.border,
        }}
      >
        <span className="text-truncate">{value || placeholder}</span>
        <FaChevronDown
          size={11}
          color="#6B7280"
          style={{
            flexShrink: 0,
            marginLeft: "8px",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        />
      </button>

      {open && (
        <div
          className="position-absolute w-100"
          style={{
            top: "calc(100% + 6px)",
            left: 0,
            background: "#fff",
            border: "1px solid #D7E0EA",
            borderRadius: "12px",
            boxShadow: "0 12px 28px rgba(15,26,43,0.16)",
            zIndex: 20,
            overflow: "hidden",
            maxHeight: "220px",
            overflowY: "auto",
          }}
        >
          {options.map((opt) => (
            <div
              key={opt}
              className="cm-dropdown-option fs-13 roboto-regular"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              style={{
                padding: "10px 14px",
                color: "#0F1A2B",
                cursor: "pointer",
                background: opt === value ? "#EAF8FB" : "#fff",
              }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}

      {error && (
        <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
          {error}
        </span>
      )}
    </div>
  );
}

function CreateMentorship({ isOpen, toggle, mode = "create", initialData = null, onSubmitted }) {
  const navigate = useNavigate();
  const { lang } = useContext(Language);
  const { updateSplash } = useContext(LoginUserData);
  const isEdit = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [focusInput, setFocusInput] = useState("");
  const [focusList, setFocusList] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [slideIn, setSlideIn] = useState(false);
  const [profileImage, setProfileImage] = useState(() => getSplashProfileImage());
  const [profileFile, setProfileFile] = useState(null);
  const [shouldChangeProfilePicture, setShouldChangeProfilePicture] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Drives the full-screen-on-small-devices layout directly via inline
  // style below. Plain JS + inline style, same pattern as ModalAddToCart.jsx
  // — reactstrap's <Offcanvas> was fought here for a while (width overrides
  // kept getting silently undone by something never tracked down even with
  // !important + computed-style inspection), so this component now renders
  // its own plain position:fixed panel instead of going through it at all.
  const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth <= 768);
  // In create mode, a backdrop click doesn't close the panel — it isn't safe
  // to discard a brand-new, not-yet-submitted application by accident — it
  // just plays a quick shake on the panel instead, to say "still here,
  // use the close button". Edit mode has nothing new to lose, so a backdrop
  // click there still closes normally.
  const [shake, setShake] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Holds the pending "actually unmount now" timer while a close animation
  // is playing. Non-null == a close is currently in flight.
  const closeTimeoutRef = useRef(null);
  // Resets `shake` back to false once the shake animation has finished
  // playing, so it can be re-triggered by the next backdrop click.
  const shakeTimeoutRef = useRef(null);
  const photoInputRef = useRef(null);
  const specialtyOptions = getSpecialtyOptions();
  const accountDetails = getAccountDetails();
  const experienceOptions = getExperienceOptions(lang?.years || "years");

  // One ref per required field, in the same top-to-bottom order they
  // appear in the form, so a missing field scrolls the user straight to it.
  const specialtyRef = useRef(null);
  const experienceRef = useRef(null);
  const institutionRef = useRef(null);
  const priceRef = useRef(null);
  const durationRef = useRef(null);
  const focusRef = useRef(null);
  const bioRef = useRef(null);
  const agreementRef = useRef(null);
  const fieldRefs = {
    specialty: specialtyRef,
    experience: experienceRef,
    institution: institutionRef,
    price: priceRef,
    duration: durationRef,
    focus: focusRef,
    bio: bioRef,
    agreement: agreementRef,
  };


  // Picking a file opens the same crop modal used on /manage-doctor-profile
  // instead of previewing the raw file directly.
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCropImageSrc(URL.createObjectURL(file));
      setCropModalOpen(true);
    }
    e.target.value = "";
  };

  // Mirrors general_profile.jsx's crop-complete handling: ImageCropModal
  // hands back a base64 data URL, which is re-fetched into a real File so it
  // can go into the update/doctor FormData payload.
  const handleCropComplete = (croppedImage) => {
    setProfileImage(croppedImage);
    fetch(croppedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
        setProfileFile(file);
      })
      .catch((err) => console.error("Error converting cropped image:", err));
  };

  // Reset to blank for a fresh application, or prefill from the submitted
  // data when reopened in edit mode — runs once per open, not on every
  // initialData change, so typing while open never gets clobbered.
  // The real, server-persisted mentor_profile from SplashData always wins
  // when it exists — `initialData` (local state from a create-mode
  // submission this session) is only a fallback for the rare case where
  // splash hasn't caught up yet, so a stale local value can never shadow
  // what's actually saved.
  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      const source = getSplashMentorFormData() || initialData;
      if (source) {
        setForm({
          specialty: source.specialty || "",
          experience: source.experience || "",
          institution: source.institution || "",
          mentorshipType: source.mentorshipType || "One-To-One",
          price: source.price || "",
          duration: source.duration || "60",
          bio: source.bio || "",
        });
        setFocusList(source.focusList || []);
      } else {
        setForm(EMPTY_FORM);
        setFocusList([]);
      }
    } else {
      setForm(EMPTY_FORM);
      setFocusList([]);
    }
    setFocusInput("");
    setAgreed(false);
    setShouldChangeProfilePicture(false);
    // Validation errors from the previous time this was open must not
    // survive a close+reopen — otherwise a field left red before closing
    // still shows red (and the stale message) on a fresh open, even though
    // the field itself was just reset to blank/prefilled above.
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Plain conditional render (`{isOpen && ...}` below) means the panel's DOM
  // node is inserted fresh the moment `isOpen` goes true, in the same commit
  // as this effect — so the double-rAF here reliably catches its initial
  // translateX(100%) paint before flipping slideIn, letting the transition
  // actually animate instead of jumping straight to open. (No reactstrap
  // Transition machinery is involved anymore, so there's no extra async
  // mount delay to account for like there used to be.)
  useEffect(() => {
    if (isOpen) {
      // A fresh open cancels any close animation still in flight (e.g. the
      // user reopens it immediately) so we don't unmount out from under them.
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      let raf2;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setSlideIn(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
      };
    }
    setSlideIn(false);
  }, [isOpen]);

  // Clean up the pending close/shake timers if the component unmounts
  // mid-animation.
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    };
  }, []);

  // reactstrap's Offcanvas used to lock body scroll and handle Escape
  // automatically — now that this renders its own plain fixed panel (see
  // the return below), both need to be done by hand. Same manual
  // scroll-lock pattern as SuccessMemeberModal.jsx and the other custom
  // modals in this app: `html` has no overflow set in main.css, so the
  // page scrollbar actually belongs to <html>, not <body> — locking body
  // alone doesn't stop it (and was leaving the page stuck non-scrollable
  // after close, since it wasn't what was actually holding the lock).
  useEffect(() => {
    if (!isOpen) return undefined;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === "Escape") requestClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Close = animate first, unmount later. Calling the parent's `toggle()`
  // immediately would flip `isOpen` to false and yank the panel out from
  // under the transition before it has a chance to slide out — this instead
  // starts the slide-out (setSlideIn(false)) and only calls the parent's
  // toggle once that transition has actually finished. Kept mounted for that
  // whole window because `isOpen` (which the `{isOpen && ...}` render below
  // checks) only flips to false once toggle() is actually called.
  const requestClose = () => {
    if (!isOpen || closeTimeoutRef.current) return;
    setSlideIn(false);
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      toggle();
    }, CLOSE_ANIMATION_MS);
  };

  // Backdrop click: in edit mode this just closes like normal (the ✕ button
  // does too). In create mode it's blocked — a stray click outside a new,
  // not-yet-submitted application shouldn't silently discard it — so it
  // shakes the panel instead as a "still open" nudge.
  const handleBackdropClick = () => {
    if (isEdit) {
      requestClose();
      return;
    }
    setShake(true);
    if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current);
    shakeTimeoutRef.current = setTimeout(() => {
      shakeTimeoutRef.current = null;
      setShake(false);
    }, 450);
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Same as handleChange, but hard-caps the field at maxChars characters —
  // paired with the input/textarea's own maxLength attribute (which is what
  // actually blocks typing/pasting past the limit, spaces included, without
  // any of the word-splitting glitches a manual word-count cap had), this
  // is just a second guard for values set programmatically (e.g. edit mode
  // prefilling from a longer saved value).
  const handleCharLimitedChange = (field, maxChars) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value.slice(0, maxChars) }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };
const handleDigitsOnlyChange = (field) => (e) => {
  const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
  setForm((prev) => ({ ...prev, [field]: digitsOnly }));
  setErrors((prev) => ({ ...prev, [field]: "" }));
};

// Belt-and-braces: block the offending keys before they even land in the
// input, so there's no visible flicker of "e" appearing then vanishing.
const blockNonDigitKeys = (e) => {
  if (["e", "E", "+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

// type="number" doesn't help with paste: pasted text like "1e5" or
// "50,000" is a browser-legal *partial* number (exponent notation), so
// e.target.value hands the "e" straight through instead of ever running it
// past handleDigitsOnlyChange — that's exactly the leftover "e" bug. Pasting
// is handled by hand instead: read the clipboard text directly, strip it to
// digits, and splice it into the current value at the cursor/selection —
// bypassing the number input's own paste handling entirely.
const handlePasteDigitsOnly = (field) => (e) => {
  e.preventDefault();
  const pasted = (e.clipboardData || window.clipboardData).getData("text").replace(/[^0-9]/g, "");
  if (!pasted) return;
  const input = e.target;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  const next = input.value.slice(0, start) + pasted + input.value.slice(end);
  setForm((prev) => ({ ...prev, [field]: next }));
  setErrors((prev) => ({ ...prev, [field]: "" }));
};
  const handleSelectChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddFocus = () => {
    const value = focusInput.trim();
    if (!value) return;
    setFocusList((prev) => [...prev, value]);
    setFocusInput("");
    setErrors((prev) => ({ ...prev, focus: "" }));
  };

  const handleFocusKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddFocus();
    }
  };

  const handleRemoveFocus = (index) => {
    setFocusList((prev) => prev.filter((_, i) => i !== index));
  };

  // Field order matches the form's top-to-bottom layout so the first error
  // found is also the first one the user actually sees.
  const validate = () => {
    const newErrors = {};
    if (!form.specialty) newErrors.specialty = lang?.mentorship_error_specialty || "Please select your specialty.";
    if (!form.experience) newErrors.experience = lang?.mentorship_error_experience || "Please select your years of experience.";
    if (!form.institution.trim()) newErrors.institution = lang?.mentorship_error_institution || "Please enter your institution.";
    if (!form.price || Number(form.price) <= 0) newErrors.price = lang?.mentorship_error_price || "Please enter your price.";
    if (!form.duration) newErrors.duration = lang?.mentorship_error_duration || "Please enter your duration.";
    if (!focusList.length) newErrors.focus = lang?.mentorship_error_focus || "Please add at least one focus area.";
    if (!form.bio.trim()) newErrors.bio = lang?.mentorship_error_bio || "Please enter your bio.";
    if (!isEdit && !agreed) {
      newErrors.agreement = lang?.mentorship_error_agreement || "Please accept the privacy policy and terms & conditions to continue.";
    }
    return newErrors;
  };

  const scrollToFirstError = (newErrors) => {
    const firstField = Object.keys(fieldRefs).find((field) => newErrors[field]);
    const target = fieldRefs[firstField]?.current;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    target.querySelector("input, textarea, button")?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      scrollToFirstError(newErrors);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("specialty", form.specialty);
      fd.append("experience", form.experience);
      fd.append("institute", form.institution);
      fd.append("type", form.mentorshipType);
      fd.append("price", form.price);
      fd.append("duration", form.duration);
      // Each focus item joined with "&&&&", e.g. "qwe&&&&test".
      fd.append("focus", focusList.join("&&&&"));
      fd.append("bio", form.bio);
      if (profileFile) fd.append("image", profileFile);
      fd.append(
        "is_update_profile",
        shouldChangeProfilePicture ? "true" : "false"
      );

      // Same payload either way — only the endpoint differs: a fresh
      // application applies, editing an existing one updates it.
      const res = await apiAdmin.post(isEdit ? "user/mentor/update" : "user/mentor/apply", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status) {
        if (isEdit) toast.success(lang?.mentorship_profile_updated_toast || "Your mentor profile has been updated.");
        // Refresh SplashData right away so BecomeMentorCard's mentor_profile
        // (and its status) reflect this submission immediately, instead of
        // waiting for the next full page load.
        try {
          const splashRes = await getSplashData();
          if (splashRes.data?.status) {
            localStorage.setItem("SplashData", JSON.stringify(splashRes.data));
            updateSplash?.(splashRes.data);
          }
        } catch (splashErr) {
          // Non-fatal — the next natural splash refetch will pick it up.
        }
        onSubmitted?.({ ...form, focusList }, mode);
        requestClose();
      } else {
        toast.error(getApiErrorMessage(res.data, lang?.mentorship_generic_error || "Something went wrong. Please try again."));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data, lang?.mentorship_generic_error || "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
          zIndex: 1050,
          opacity: slideIn ? 1 : 0,
          transition: `opacity ${CLOSE_ANIMATION_MS}ms ease-in-out`,
        }}
      />

      {/* Slide-in panel */}
      <div
        className="mentorship-page"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: isSmallScreen ? "100%" : "550px",
          maxWidth: isSmallScreen ? "100%" : "550px",
          background: "#fff",
          zIndex: 1051,
          display: "flex",
          flexDirection: "column",
          borderTopLeftRadius: isSmallScreen ? 0 : "20px",
          borderBottomLeftRadius: isSmallScreen ? 0 : "20px",
          overflow: "hidden",
          transform: slideIn ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${CLOSE_ANIMATION_MS}ms ease-in-out`,
          animation: shake ? "cmPanelShake 0.45s ease-in-out" : "none",
        }}
      >
        <style>{`
          .cm-dropdown-option:hover {
            background-color: #EAF8FB !important;
            color: #0F1A2B !important;
          }
          .cm-link:hover {
            text-decoration: underline !important;
          }
          /* Smooth "still open, use the ✕ to close" nudge — plays on a
             backdrop click while in create mode instead of closing. */
          @keyframes cmPanelShake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-10px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(4px); }
          }
          @media (prefers-reduced-motion: reduce) {
            .mentorship-page { animation: none !important; }
          }
        `}</style>
        <div className="p-4" style={{ padding: "28px", overflowY: "auto", flex: 1 }}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="roboto-bold fs-20 mb-0" style={{ color: "#0F1A2B" }}>
              {isEdit
                ? lang?.mentorship_edit_profile_title || "Edit Mentor Profile"
                : lang?.mentorship_become_mentor_title || "Request to become a mentor"}
            </h5>
            <button
              type="button"
              onClick={requestClose}
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "50%",
                background: "#fff",
                border:"1px solid #F7F7F7",
                boxShadow: "0 2px 8px rgba(28, 37, 51, 0.16)",
              }}
            >
              <IoClose size={18} color="#5F6673" />
            </button>
          </div>
          <p className="fs-13" style={{ color: "#5F6673", fontWeight:"500", lineHeight: "20px" }}>
            {isEdit
              ? lang?.mentorship_edit_profile_subtitle || "Update your professional information, biography, specialties, availability, and other details displayed to prospective mentees."
              : lang?.mentorship_become_mentor_subtitle || "Tell us about your expertise and our team will review your application and send you a tailored proposal."}
          </p>

          {/* Account details */}
          <span
            className="fs-13  d-block mb-2"
            style={{ color: "#0E1421",fontWeight:"500", letterSpacing: "0.5px" }}
          >
            {lang?.mentorship_account_details_title || "YOUR ACCOUNT DETAILS"}
          </span>
          <div
            className="position-relative mb-4"
            style={{ background: "#1DC1DD1A", borderRadius: "20px", border:"1px solid #E6E7EA", padding: "16px" }}
          >
            <button
              type="button"
              onClick={() => navigate(DOCTOR_ACCOUNT)}
              className="position-absolute d-flex align-items-center justify-content-center"
              style={{
                top: "12px",
                right: "12px",
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                border:"1px solid #D7E0EA",
                background: "#F9FAFA",
              }}
            >
             <GoPencil style={{ color: "#6D7683" }} />
            </button>

            <div className="row gx-3 gy-3">
              <div className="col-6">
                <span
                  className="fs-12 d-block"
                  style={{ color: "#5F6673", fontWeight:"400", letterSpacing: "0.4px" }}
                >
                  {lang?.mentorship_full_name_label || "FULL NAME"}
                </span>
                <span
                  className="fs-13 d-block"
                  style={{ color: "#0E1421", fontWeight: "500", lineHeight: "1.35" }}
                >
                  {accountDetails.fullName}
                </span>
              </div>
              <div className="col-6">
                <span
                  className="fs-12 d-block"
               style={{ color: "#5F6673", fontWeight:"400", letterSpacing: "0.4px" }}
                >
                  {lang?.mentorship_work_email_label || "WORK EMAIL"}
                </span>
                <span
                  className="fs-13 text-truncate d-block"
                  style={{ color: "#0E1421", fontWeight: "500", lineHeight: "1.35" }}
                  title={accountDetails.workEmail}
                >
                  {accountDetails.workEmail}
                </span>
              </div>
              <div className="col-6">
                <span
                  className="fs-12 d-block"
                style={{ color: "#5F6673", fontWeight:"400", letterSpacing: "0.4px" }}
                >
                  {lang?.mentorship_phone_label || "PHONE"}
                </span>
                <span
                  className="fs-13 d-flex align-items-center gap-2"
                  style={{ color: "#0E1421", fontWeight: "500", lineHeight: "1.35" }}
                >
                  {accountDetails.countryCode}
                  <span style={{ width: "1px", height: "14px", background: "#D7E0EA" }} />
                  {accountDetails.phone}
                </span>
              </div>
              <div className="col-6">
                <span
                  className="fs-12  d-block"
                style={{ color: "#5F6673", fontWeight:"400", letterSpacing: "0.4px" }}
                >
                  {lang?.mentorship_country_label || "COUNTRY"}
                </span>
                <span
                  className="fs-13 text-truncate d-block"
                  style={{ color: "#0E1421", fontWeight: "500", lineHeight: "1.35" }}
                  title={accountDetails.country || undefined}
                >
                  {accountDetails.country || "__"}
                </span>
              </div>
            </div>
          </div>

          {/* Profile photo container */}
          <div
            className="mb-3"
            style={{
              border: "1px solid #D7E0EA",
              borderRadius: "16px",
              padding: "14px 16px",
              background: "#FFFFFF",
            }}
          >
            {/* Top Row: Avatar + Info + Upload/Change Button */}
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "0.5px solid #DCE5EE",
                    background: profileImage ? "transparent" : "#F9FAFA",
                  }}
                >
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <RxPerson size={22} color="#98A2B3" />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <span className="fs-13 d-block" style={{ color: "#031316", fontWeight: "500" }}>
                    {lang?.mentorship_add_profile_photo || "Add a profile photo"}
                  </span>
                  <span className="fs-11 d-block" style={{ color: "#5F6673", fontWeight: "400" , textAlign:"justify"}}>
                    {profileImage
                      ? lang?.mentorship_photo_shown_desc || "Shown to mentees on your public profile"
                      : lang?.mentorship_photo_help_desc || "Help mentees find and connect with you."}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="flex-shrink-0"
                style={
                  profileImage
                    ? {
                        border: "0.5px solid #00BADA",
                        color: "#111",
                        background: "#1DC1DD0A",
                        borderRadius: "30px",
                        padding: "6px 16px",
                        fontWeight: "600",
                        fontSize: "12px",
                      }
                    : {
                        border: "none",
                        color: "#fff",
                        background: "#1DC1DD",
                        borderRadius: "30px",
                        padding: "8px 18px",
                        fontWeight: "700",
                        fontSize: "12px",
                      }
                }
              >
                {profileImage
                  ? lang?.mentorship_change_photo || "Change Photo"
                  : lang?.mentorship_upload_photo || "Upload Photo"}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePhotoChange}
              />
            </div>

            {/* Checkbox Row inside container */}
            <div
              className="d-flex align-items-center"
              style={{
                borderTop: "1px solid #EEF2F6",
                margin: "12px -16px 0 -16px",
                padding: "10px 16px 0 16px",
                gap: "8px",
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => setShouldChangeProfilePicture((prev) => !prev)}
            >
              <button
                type="button"
                aria-pressed={shouldChangeProfilePicture}
                aria-label={lang?.mentorship_update_profile_picture_also || "Also update my profile photo"}
                onClick={(e) => {
                  e.stopPropagation();
                  setShouldChangeProfilePicture((prev) => !prev);
                }}
                className="d-inline-flex align-items-center justify-content-center p-0 flex-shrink-0"
                style={{
                  width: "14px",
                  height: "14px",
                  minWidth: "14px",
                  minHeight: "14px",
                  borderRadius: "3px",
                  border: shouldChangeProfilePicture ? "1.5px solid #00BADA" : "1.5px solid #D0D5DD",
                  background: shouldChangeProfilePicture ? "#00BADA" : "#fff",
                  color: "#fff",
                  cursor: "pointer",
                  lineHeight: 0,
                  margin: 0,
                  marginTop: "1.5px",
                  transition: "all 0.15s ease-in-out",
                }}
              >
                {shouldChangeProfilePicture && <FaCheck size={7} color="#fff" />}
              </button>
              <span
                style={{
                  color: "#5F6673",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "1.3",
                  display: "inline-block",
                }}
              >
                {lang?.mentorship_update_profile_picture_also || "Also update my profile photo"}
              </span>
            </div>

          </div>




          <ImageCropModal
            isOpen={cropModalOpen}
            toggle={() => setCropModalOpen(false)}
            imageSrc={cropImageSrc}
            aspect={1}
            onCropComplete={handleCropComplete}
          />

          {/* Specialty / Years of experience */}
          <div className="d-flex gap-3 mb-3">
            <div ref={specialtyRef} style={{ flex: "1 1 0%", minWidth: 0 }}>
              <span className={labelClass} style={{ color: "#0F1A2B" }}>
                {lang?.mentorship_specialty_label || "Specialty"}
              </span>
              <CustomSelect
                value={form.specialty}
                onChange={handleSelectChange("specialty")}
                options={specialtyOptions}
                placeholder={lang?.mentorship_select_specialty_placeholder || "Select specialty"}
                error={errors.specialty}
              />
            </div>
            <div ref={experienceRef} style={{ flex: "1 1 0%", minWidth: 0 }}>
              <span className={labelClass} style={{ color: "#0F1A2B" }}>
                {lang?.mentorship_experience_label || "Years of experience"}
              </span>
              <CustomSelect
                value={form.experience}
                onChange={handleSelectChange("experience")}
                options={experienceOptions}
                placeholder={lang?.mentorship_select_experience_placeholder || "Select years of experience"}
                error={errors.experience}
              />
            </div>
          </div>

          {/* Institution */}
          <div ref={institutionRef} className="mb-3">
            <span className={labelClass} style={{ color: "#0F1A2B" }}>
              {lang?.mentorship_institution_label || "Institution"}
            </span>
            <input
              type="text"
              className="roboto-regular"
              maxLength={200}
              style={{ ...inputStyle, border: errors.institution ? "1px solid #ef4444" : inputStyle.border }}
              value={form.institution}
              onChange={handleCharLimitedChange("institution", 200)}
              placeholder={lang?.mentorship_write_institute_placeholder || "Write institute"}
            />
            {/* <span className="fs-11 d-block" style={{ color: "#98A2B3", marginTop: "4px" }}>
              {form.institution.length}/50
            </span> */}
            {errors.institution && (
              <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                {errors.institution}
              </span>
            )}
          </div>

          {/* Mentorship type — only "One-To-One" is offered today, but this
              renders as a real dropdown (chevron included) so a second type
              can be added later just by extending the options array. */}
          <div className="mb-3">
            <span className={labelClass} style={{ color: "#0F1A2B" }}>
              {lang?.mentorship_type_label || "Mentorship type"}
            </span>
            <CustomSelect
              value={form.mentorshipType}
              onChange={handleSelectChange("mentorshipType")}
              options={MENTORSHIP_TYPE_OPTIONS}
              placeholder={lang?.mentorship_select_type_placeholder || "Select mentorship type"}
              error={errors.mentorshipType}
            />
          </div>

          {/* Price / Duration */}
          <div className="d-flex gap-3 mb-3">
            <div ref={priceRef} className="flex-fill">
              <span className={labelClass} style={{ color: "#0F1A2B" }}>
                {lang?.mentorship_price_label || "Price"}
              </span>
              <div className="position-relative">
               <input
  type="number"
  min="0"
  className="roboto-regular"
  style={{ ...inputStyle, paddingRight: "40px", border: errors.price ? "1px solid #ef4444" : inputStyle.border }}
  value={form.price}
  onChange={handleDigitsOnlyChange("price")}
  onKeyDown={blockNonDigitKeys}
  onPaste={handlePasteDigitsOnly("price")}
  placeholder="0"
/>
                <span
                  className="fs-12 roboto-regular position-absolute"
                  style={{
                    right: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#848798",
                  }}
                >
                  (€)
                </span>
              </div>
              {errors.price && (
                <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                  {errors.price}
                </span>
              )}
            </div>
            <div ref={durationRef} className="flex-fill">
              <span className={labelClass} style={{ color: "#0F1A2B" }}>
                {lang?.mentorship_duration_label || "Duration"}
              </span>
              <div className="position-relative">
               <input
  type="number"
  min="0"
  className="roboto-regular"
  style={{ ...inputStyle, paddingRight: "62px", border: errors.duration ? "1px solid #ef4444" : inputStyle.border }}
  value={form.duration}
  onChange={handleDigitsOnlyChange("duration")}
  onKeyDown={blockNonDigitKeys}
  onPaste={handlePasteDigitsOnly("duration")}
  placeholder="0"
/>
                <span
                  className="fs-12 roboto-regular position-absolute"
                  style={{
                    right: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#848798",
                  }}
                >
                  {lang?.mentorship_minutes_suffix || "minutes"}
                </span>
              </div>
              {errors.duration && (
                <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                  {errors.duration}
                </span>
              )}
            </div>
          </div>

          {/* Focus */}
          <div ref={focusRef} className="mb-3">
            <span className={labelClass} style={{ color: "#0F1A2B" }}>
              {lang?.mentorship_focus_label || "Focus"}
            </span>
            <div
              className="d-flex align-items-center mb-2"
              style={{ border: errors.focus ? "1px solid #ef4444" : "1px solid #D7E0EA", borderRadius: "12px" }}
            >
              <input
                type="text"
                className="roboto-regular flex-fill"
                placeholder={lang?.mentorship_write_focus_placeholder || "Write Focus"}
                value={focusInput}
                onChange={(e) => setFocusInput(e.target.value)}
                onKeyDown={handleFocusKeyDown}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  padding: "10px 14px",
                  fontSize: "13px",
                  color: "#0F1A2B",
                  minWidth: 0,
                }}
              />
              <button
                type="button"
                onClick={handleAddFocus}
                className="d-flex align-items-center gap-1 border-0 bg-transparent flex-shrink-0 fs-13 roboto-bold"
                style={{ color: "#00BADA", padding: "10px 14px" }}
              >
                <FaPlus size={10} /> {lang?.mentorship_add_focus || "Add"}
              </button>
            </div>

            {focusList.map((item, index) => (
              <div
                key={`${item}-${index}`}
                className="d-flex align-items-center justify-content-between mb-2"
                style={{
                  border: "1px solid #E4E4E7",
                  borderRadius: "12px",
                  padding: "10px 14px",
                }}
              >
                <span
                  className="fs-13 roboto-regular text-truncate me-2"
                  style={{ color: "#0F1A2B", minWidth: 0 }}
                  title={item}
                >
                  {item}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveFocus(index)}
                  className="border-0 bg-transparent d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ color: "#848798" }}
                >
                  <IoClose size={16} />
                </button>
              </div>
            ))}

            {errors.focus && (
              <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                {errors.focus}
              </span>
            )}
          </div>

          {/* Bio */}
          <div ref={bioRef} className="mb-4">
            <span className={labelClass} style={{ color: "#0F1A2B" }}>
              {lang?.mentorship_bio_label || "Bio"}
            </span>
            <textarea
              className="roboto-regular"
              rows={4}
              maxLength={500}
              style={{ ...inputStyle, resize: "none", lineHeight: "20px", border: errors.bio ? "1px solid #ef4444" : inputStyle.border }}
              value={form.bio}
              onChange={handleCharLimitedChange("bio", 500)}
              placeholder={lang?.mentorship_bio_placeholder || "Briefly share your goals, availability, anything you'd like us to know about your mentorship approach."}
            />
            {/* <span className="fs-11 d-block" style={{ color: "#98A2B3", marginTop: "4px" }}>
              {form.bio.length}/500
            </span> */}
            {errors.bio && (
              <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                {errors.bio}
              </span>
            )}
          </div>

          {!isEdit && (
            <>
              {/* Divider */}
              <div style={{ borderTop: "1px solid #EAEEF2", margin: "0 0 20px" }} />

              {/* Agreement */}
              <div ref={agreementRef} className="mb-3">
                <div className="d-flex align-items-start gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAgreed((prev) => !prev);
                      setErrors((prev) => ({ ...prev, agreement: "" }));
                    }}
                    aria-pressed={agreed}
                    className="d-flex align-items-center justify-content-center p-0 flex-shrink-0"
                    style={{
                      width: "18px",
                      height: "18px",
                      marginTop: "1px",
                      borderRadius: "5px",
                      border: `1.5px solid ${errors.agreement ? "#ef4444" : agreed ? "#00BADA" : "#C7CFDA"}`,
                      background: agreed ? "#E6F8FB" : "#fff",
                    }}
                  >
                    {agreed && <FaCheck size={9} color="#00BADA" />}
                  </button>
                  <label
                    className="fs-12 roboto-regular mb-0"
                    style={{ color: "#5F6673", lineHeight: "18px", cursor: "pointer" }}
                    onClick={() => {
                      setAgreed((prev) => !prev);
                      setErrors((prev) => ({ ...prev, agreement: "" }));
                    }}
                  >
                    {lang?.mentorship_agree_to_prefix || "I agree to the"}{" "}
                    <a
                      href="https://medicalradar.es/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="roboto-medium cm-link"
                      style={{ color: "#00BADA" }}
                    >
                      {lang?.privacy_policy || "Privacy Policy"}
                    </a>
                    ,{" "}
                    <a
                      href="https://medicalradar.es/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="roboto-medium cm-link"
                      style={{ color: "#00BADA" }}
                    >
                      {lang?.terms_conditions || "Terms & Conditions"}
                    </a>
                    {lang?.mentorship_agree_data_processing_suffix || ", and the processing of my personal data for the mentor application review process."}
                  </label>
                </div>
                {errors.agreement && (
                  <span className="fs-11 d-block" style={{ color: "#ef4444", marginTop: "4px" }}>
                    {errors.agreement}
                  </span>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div
            className={`d-flex align-items-center gap-2 ${isEdit ? "justify-content-end" : "justify-content-between"}`}
            style={isEdit ? { marginTop: "40px" } : undefined}
          >
            {!isEdit && (
              <p
                className="fs-12 roboto-regular mb-0"
                style={{ color: "#848798", minWidth: 0, flexShrink: 1 }}
              >
                {lang?.mentorship_or_email_us_at || "Or email us at"}{" "}
                <a
                  href="mailto:info@ophthalmologymentorship.com"
                  className="roboto-medium cm-link"
                  style={{ color: "#00BADA" }}
                >
                  info@ophthalmologymentorship.com
                </a>
              </p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="rounded-pill text-white border-0 fs-13 roboto-bold"
              style={{ background: "#00BADA", padding: "6px 20px", flexShrink: 0, whiteSpace: "nowrap", opacity: submitting ? 0.7 : 1 }}
            >
              {submitting
                ? lang?.submitting || "Submitting…"
                : isEdit
                ? lang?.save_changes || "Save Changes"
                : lang?.mentorship_send_request || "Send Request"}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>
  );
}

export default React.memo(CreateMentorship);
